// Static-mode auth — login is purely client-side.
// Validates against a small list of family members + master password "Doby"
// or each member's personal password.

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { readKosherMode, writeKosherMode } from "./kosher";

const MEMBERS_PASSWORDS = {
  papa:     "Doby",
  mama:     "Doby",
  tati:     "Tati",
  lucho:    "Lucho",
  tairuchi: "Tairuchi",
  liuchi:   "Liuchi",
  invitado: "Doby", // Familia & Amigos — misma clave que papá/mamá
};
const MASTER = "Doby";

const AuthCtx = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [member, setMember] = useState(null);
  const [guestName, setGuestName] = useState(null);
  const [kosherMode, setKosherMode] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem("recetario_token");
    const m = localStorage.getItem("recetario_member");
    const gn = localStorage.getItem("recetario_guest_name");
    if (t) {
      setToken(t);
      setMember(m || "papa");
      if (gn) setGuestName(gn);
      // Rehidrata el kosher mode del miembro/invitado
      setKosherMode(readKosherMode(m || "papa", gn || ""));
    }
    setReady(true);
  }, []);

  const login = useCallback(async (password, memberName, guestNameArg, kosherArg) => {
    const m = (memberName || "").toLowerCase();
    const correct = MEMBERS_PASSWORDS[m];
    if (!correct) throw new Error("Miembro desconocido");
    if (password !== correct && password !== MASTER) {
      const err = new Error("Contraseña incorrecta");
      err.response = { data: { detail: "Contraseña incorrecta" } };
      throw err;
    }
    if (m === "invitado") {
      const clean = String(guestNameArg || "").trim();
      if (!clean) {
        const err = new Error("Cuéntanos tu nombre");
        err.response = { data: { detail: "Cuéntanos tu nombre" } };
        throw err;
      }
      localStorage.setItem("recetario_guest_name", clean);
      setGuestName(clean);
    } else {
      localStorage.removeItem("recetario_guest_name");
      setGuestName(null);
    }
    // Persistir kosher mode elegido en login pa' este miembro/invitado
    const kosher = !!kosherArg;
    writeKosherMode(m, guestNameArg || "", kosher);
    setKosherMode(kosher);
    // No JWT needed in static mode — just a marker
    const fakeToken = `static_${m}_${Date.now()}`;
    localStorage.setItem("recetario_token", fakeToken);
    localStorage.setItem("recetario_member", m);
    setToken(fakeToken);
    setMember(m);
    return { token: fakeToken, member: m };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("recetario_token");
    localStorage.removeItem("recetario_member");
    localStorage.removeItem("recetario_guest_name");
    setToken(null);
    setMember(null);
    setGuestName(null);
  }, []);

  // Nombre para mostrar en saludos: usa el nombre del invitado si aplica.
  const displayName = (() => {
    if (member === "invitado" && guestName) return guestName;
    const LABELS = {
      papa: "Papá", mama: "Mamá",
      tati: "Tati", lucho: "Lucho", tairuchi: "Tairuchi", liuchi: "Liuchi",
    };
    return LABELS[member] || member || null;
  })();

  return <AuthCtx.Provider value={{ token, member, guestName, displayName, kosherMode, login, logout, ready }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

// Compatibility shim: pages still call `api()` — return our static client.
import { staticApi } from "./staticApi";
export function api() { return staticApi; }

// Some pages reference API as a base URL — keep an empty value so old code doesn't break.
export const API = "";
